import React, { useState } from "react";
import { Mail, Phone, MapPin, Clock, CheckCircle2, Send } from "lucide-react";
import Navbar from "../../components/navbar";
import Footer from "../../components/footer";
import service from "../../Service/Service";
import "./ContactPage.css";

const CONTACT = {
  phone: "03338820922",
  phoneDisplay: "0333 8820922",
  email: "support@skysale.pk",
  address: "32 IT tower, Link road model town, Lahore",
};

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg("");
    try {
      await service.addContact(form);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      setStatus("error");
      setErrorMsg("Something went wrong. Please try again or call us directly.");
    }
  };

  return (
    <div>
      <Navbar bg={1} />
      <div className="contact-page">
        {/* Hero */}
        <div className="contact-hero">
          <div className="contact-hero-glow-1" />
          <div className="contact-hero-glow-2" />
          <div className="contact-hero-inner">
            <span className="contact-hero-badge">✉️ We'd Love to Hear From You</span>
            <h1 className="contact-hero-title">Get In Touch</h1>
            <p className="contact-hero-text">
              Our travel experts are ready to help you plan your perfect trip.
            </p>
          </div>
        </div>

        <div className="contact-body">
          <div className="contact-grid">
            {/* Contact Form */}
            <div className="contact-form-card">
              {status === "success" ? (
                <div className="contact-success">
                  <CheckCircle2 />
                  <h2 className="contact-success-title">Message Sent!</h2>
                  <p className="contact-success-text">
                    Thank you for reaching out. One of our travel experts will get back to you within 24 hours.
                  </p>
                  <button
                    type="button"
                    className="contact-success-again"
                    onClick={() => setStatus("idle")}
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  <h2 className="contact-form-title">Send us a message</h2>

                  {status === "error" && (
                    <div className="contact-error">{errorMsg}</div>
                  )}

                  <div className="contact-form-grid">
                    <div className="contact-field">
                      <label>
                        Full Name <span className="req">*</span>
                      </label>
                      <input
                        name="name"
                        required
                        value={form.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                      />
                    </div>
                    <div className="contact-field">
                      <label>
                        Email Address <span className="req">*</span>
                      </label>
                      <input
                        name="email"
                        type="email"
                        required
                        value={form.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                      />
                    </div>
                    <div className="contact-field">
                      <label>Phone Number</label>
                      <input
                        name="phone"
                        type="tel"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+92 300 0000000"
                      />
                    </div>
                    <div className="contact-field">
                      <label>
                        Subject <span className="req">*</span>
                      </label>
                      <select
                        name="subject"
                        required
                        value={form.subject}
                        onChange={handleChange}
                      >
                        <option value="">Select a subject</option>
                        <option>Flight Enquiry</option>
                        <option>Package Enquiry</option>
                        <option>Umrah Package</option>
                        <option>Booking Support</option>
                        <option>General Question</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="contact-field">
                    <label>
                      Message <span className="req">*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={6}
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Tell us how we can help you..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="contact-submit"
                  >
                    {status === "submitting" ? (
                      <>
                        <span className="contact-spinner" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="contact-sidebar">
              <div className="contact-card">
                <h2 className="contact-card-title">Contact Information</h2>
                <div className="contact-info-list">
                  <a href={`tel:${CONTACT.phone}`} className="contact-info-item">
                    <div className="contact-info-icon">
                      <Phone />
                    </div>
                    <div>
                      <p className="contact-info-label">Phone</p>
                      <p className="contact-info-value">{CONTACT.phoneDisplay}</p>
                    </div>
                  </a>
                  <a href={`mailto:${CONTACT.email}`} className="contact-info-item">
                    <div className="contact-info-icon">
                      <Mail />
                    </div>
                    <div>
                      <p className="contact-info-label">Email</p>
                      <p className="contact-info-value">{CONTACT.email}</p>
                    </div>
                  </a>
                  <div className="contact-info-item">
                    <div className="contact-info-icon">
                      <MapPin />
                    </div>
                    <div>
                      <p className="contact-info-label">Address</p>
                      <p className="contact-info-value">{CONTACT.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="contact-card">
                <h2 className="contact-card-title with-icon">
                  <Clock />
                  Opening Hours
                </h2>
                <div className="contact-hours">
                  <div className="contact-hours-row">
                    <span className="contact-hours-day">Mon – Fri</span>
                    <span className="contact-hours-time">9:00 – 18:00</span>
                  </div>
                  <div className="contact-hours-row">
                    <span className="contact-hours-day">Saturday</span>
                    <span className="contact-hours-time">10:00 – 16:00</span>
                  </div>
                  <div className="contact-hours-row">
                    <span className="contact-hours-day">Sunday</span>
                    <span className="contact-hours-time closed">Closed</span>
                  </div>
                </div>
              </div>

              <div className="contact-help-card">
                <h3 className="contact-help-title">Need immediate help?</h3>
                <p className="contact-help-text">
                  Call us directly and a travel expert will assist you right away.
                </p>
                <a href={`tel:${CONTACT.phone}`} className="contact-help-btn">
                  <Phone />
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Contact;
